import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Link } from "react-router-dom";

const BANNER_W = 1584;
const BANNER_H = 396;

const NAOMI_PHOTO =
    "https://customer-assets.emergentagent.com/job_gateway-one-1/artifacts/006kknlr_FullLogo.png";

const PRESETS = {
    authority: {
        label: "Authority — with photo",
        preview: ["#FDFBF7", "#E8E0D4"],
    },
    split: {
        label: "Split — ivory + ink",
        preview: ["#FDFBF7", "#171614"],
    },
    ivory: {
        label: "Ivory editorial",
        preview: ["#FDFBF7", "#FDFBF7"],
    },
    ink: {
        label: "Ink monolith",
        preview: ["#171614", "#171614"],
    },
};

export default function Studio() {
    const bannerRef = useRef(null);
    const [variant, setVariant] = useState("authority");
    // Authority variant fields
    const [name, setName] = useState("Naomi Lennon");
    const [role, setRole] = useState("Creator economy operator");
    const [credentials, setCredentials] = useState(
        "Founder, Lennon Management — acquired 2019 · Advisor to creators, AI platforms, publishers, and global brands",
    );
    const [photoUrl, setPhotoUrl] = useState(NAOMI_PHOTO);
    // Split / ivory / ink variant fields (kept from the previous design)
    const [line1, setLine1] = useState("Sign slower.");
    const [line2, setLine2] = useState("Understand deeper.");
    const [line3, setLine3] = useState("Build bigger.");
    const [right1, setRight1] = useState("I built one of the first");
    const [right2, setRight2] = useState("creator management");
    const [right3, setRight3] = useState("companies in the world.");
    const [downloading, setDownloading] = useState(false);

    const onDownload = async () => {
        if (!bannerRef.current) return;
        setDownloading(true);
        try {
            const dataUrl = await toPng(bannerRef.current, {
                pixelRatio: 2,
                cacheBust: true,
                backgroundColor: "#FDFBF7",
            });
            const a = document.createElement("a");
            a.download = `naomi-lennon-linkedin-banner-${variant}.png`;
            a.href = dataUrl;
            a.click();
        } catch (e) {
            // eslint-disable-next-line no-alert
            alert("Couldn’t generate the image. Try again.");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <main className="min-h-screen bg-ivory text-ink py-12 md:py-16 px-6 md:px-12">
            <div className="max-w-7xl mx-auto">
                <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted mb-3">
                            Studio · LinkedIn banner generator
                        </p>
                        <h1 className="font-display text-4xl md:text-6xl font-light leading-none tracking-tighter">
                            Generate your <em className="italic text-rust">banner.</em>
                        </h1>
                        <p className="mt-4 font-sans text-base text-ink-muted max-w-xl">
                            1584 × 396 px · matches the site brand exactly · downloads as
                            high-resolution PNG.
                        </p>
                    </div>
                    <Link
                        to="/"
                        className="font-mono text-[11px] uppercase tracking-[0.2em] border border-ink px-6 py-3 hover:bg-ink hover:text-ivory transition-colors duration-300"
                    >
                        ← Back to site
                    </Link>
                </div>

                {/* Variant picker */}
                <div className="mb-10">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-4">
                        Variant
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {Object.entries(PRESETS).map(([key, v]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setVariant(key)}
                                className={`flex items-center gap-4 p-3 border transition-colors text-left ${
                                    variant === key
                                        ? "border-ink bg-ink-border/30"
                                        : "border-ink-border hover:border-ink"
                                }`}
                            >
                                <div className="flex w-14 h-7 border border-ink-border flex-shrink-0">
                                    <div className="flex-1" style={{ background: v.preview[0] }} />
                                    <div className="flex-1" style={{ background: v.preview[1] }} />
                                </div>
                                <span className="font-mono text-[11px] uppercase tracking-[0.2em]">
                                    {v.label}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Variant-specific controls */}
                {variant === "authority" && (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
                        <div className="lg:col-span-4">
                            <Field label="Name" value={name} onChange={setName} />
                        </div>
                        <div className="lg:col-span-4">
                            <Field label="Role" value={role} onChange={setRole} />
                        </div>
                        <div className="lg:col-span-4">
                            <Field
                                label="Photo URL (square or portrait)"
                                value={photoUrl}
                                onChange={setPhotoUrl}
                            />
                        </div>
                        <div className="lg:col-span-12">
                            <Field
                                label="Credentials line"
                                value={credentials}
                                onChange={setCredentials}
                            />
                        </div>
                    </div>
                )}

                {variant !== "authority" && (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
                        <div className="lg:col-span-6">
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-4">
                                Headline (left)
                            </p>
                            <div className="space-y-3">
                                <Field label="Line 1" value={line1} onChange={setLine1} />
                                <Field label="Line 2" value={line2} onChange={setLine2} />
                                <Field label="Line 3 (italic)" value={line3} onChange={setLine3} />
                            </div>
                        </div>
                        <div className="lg:col-span-6">
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-4">
                                Statement (right)
                            </p>
                            <div className="space-y-3">
                                <Field label="Line 1" value={right1} onChange={setRight1} />
                                <Field label="Line 2 (accent)" value={right2} onChange={setRight2} />
                                <Field label="Line 3" value={right3} onChange={setRight3} />
                            </div>
                        </div>
                    </div>
                )}

                {/* Banner preview */}
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-4">
                    Preview · displayed at 50% — exports at native 1584 × 396 px
                </p>

                <div className="overflow-x-auto border border-ink-border bg-ink-border/20 p-6 mb-8">
                    <div style={{ width: BANNER_W / 2, height: BANNER_H / 2, overflow: "hidden" }}>
                        <div
                            style={{
                                width: BANNER_W,
                                height: BANNER_H,
                                transform: "scale(0.5)",
                                transformOrigin: "top left",
                            }}
                        >
                            <BannerCanvas
                                innerRef={bannerRef}
                                variant={variant}
                                name={name}
                                role={role}
                                credentials={credentials}
                                photoUrl={photoUrl}
                                line1={line1}
                                line2={line2}
                                line3={line3}
                                right1={right1}
                                right2={right2}
                                right3={right3}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-between flex-wrap gap-4">
                    <p className="font-sans text-sm text-ink-muted max-w-xl">
                        Click download → upload directly to LinkedIn → Edit profile →
                        Background photo.
                    </p>
                    <button
                        type="button"
                        onClick={onDownload}
                        disabled={downloading}
                        className="inline-flex items-center justify-center bg-ink text-ivory font-mono text-xs uppercase tracking-[0.2em] px-10 py-5 hover:bg-rust transition-colors duration-300 disabled:opacity-60"
                    >
                        {downloading ? "Rendering…" : "Download banner PNG →"}
                    </button>
                </div>
            </div>
        </main>
    );
}

function Field({ label, value, onChange }) {
    return (
        <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                {label}
            </label>
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-transparent border-b border-ink px-1 py-2 font-sans text-base text-ink focus:outline-none focus:border-rust transition-colors"
            />
        </div>
    );
}

function BannerCanvas({
    innerRef,
    variant,
    name,
    role,
    credentials,
    photoUrl,
    line1,
    line2,
    line3,
    right1,
    right2,
    right3,
}) {
    const fontDisplay = '"Cormorant Garamond", Georgia, "Times New Roman", serif';
    const fontMono = '"JetBrains Mono", ui-monospace, monospace';

    if (variant === "authority") {
        return (
            <div
                ref={innerRef}
                style={{
                    width: BANNER_W,
                    height: BANNER_H,
                    display: "flex",
                    backgroundColor: "#FDFBF7",
                    fontFamily: '"Manrope", system-ui, sans-serif',
                }}
            >
                {/* LEFT — empty safe zone where LinkedIn drops the circular profile photo */}
                <div style={{ flex: "0 0 380px" }} />

                {/* MIDDLE — name + credentials, oversized so it stays readable when LinkedIn scales the banner */}
                <div
                    style={{
                        flex: "1 1 auto",
                        padding: "44px 56px 44px 32px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        color: "#171614",
                    }}
                >
                    <p
                        style={{
                            fontFamily: fontMono,
                            fontSize: "16px",
                            fontWeight: 600,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            color: "#171614",
                            margin: 0,
                            marginBottom: "20px",
                        }}
                    >
                        naomilennon.com
                    </p>
                    <h2
                        style={{
                            fontFamily: fontDisplay,
                            fontWeight: 600,
                            fontSize: "120px",
                            lineHeight: 0.92,
                            letterSpacing: "-0.03em",
                            margin: 0,
                            color: "#171614",
                        }}
                    >
                        {name}
                    </h2>
                    <p
                        style={{
                            fontFamily: fontDisplay,
                            fontStyle: "italic",
                            fontWeight: 500,
                            fontSize: "40px",
                            letterSpacing: "-0.005em",
                            color: "#3A3733",
                            margin: 0,
                            marginTop: "14px",
                        }}
                    >
                        {role}
                    </p>
                    <div
                        style={{
                            width: "72px",
                            height: "3px",
                            backgroundColor: "#B23B22",
                            margin: "22px 0 18px 0",
                        }}
                    />
                    <p
                        style={{
                            fontFamily: fontDisplay,
                            fontStyle: "italic",
                            fontWeight: 500,
                            fontSize: "26px",
                            letterSpacing: "-0.005em",
                            color: "#171614",
                            lineHeight: 1.35,
                            margin: 0,
                            maxWidth: "700px",
                        }}
                    >
                        {credentials}
                    </p>
                </div>

                {/* RIGHT — portrait */}
                <div
                    style={{
                        flex: "0 0 420px",
                        backgroundColor: "#E8E0D4",
                        position: "relative",
                        overflow: "hidden",
                        borderLeft: "1px solid #DCD5C9",
                    }}
                >
                    {photoUrl && (
                        <img
                            src={photoUrl}
                            alt={name}
                            crossOrigin="anonymous"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center 18%",
                                display: "block",
                            }}
                        />
                    )}
                </div>
            </div>
        );
    }

    // Existing split / ivory / ink variants (typography-only)
    const isInk = variant === "ink";
    const isIvory = variant === "ivory";
    const isSplit = variant === "split";
    const bgLeft = isInk ? "#171614" : "#FDFBF7";
    const bgRight = isIvory ? "#FDFBF7" : "#171614";
    const textLeft = isInk ? "#FDFBF7" : "#171614";
    const textRight = isIvory ? "#171614" : "#FDFBF7";
    const dim = isInk ? "rgba(253,251,247,0.55)" : "rgba(23,22,20,0.55)";

    return (
        <div
            ref={innerRef}
            style={{
                width: BANNER_W,
                height: BANNER_H,
                display: "flex",
                position: "relative",
                fontFamily: '"Manrope", system-ui, sans-serif',
            }}
        >
            <div
                style={{
                    flex: isSplit ? "0 0 60%" : "1",
                    backgroundColor: bgLeft,
                    padding: "56px 64px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    color: textLeft,
                }}
            >
                <p
                    style={{
                        fontFamily: fontMono,
                        fontSize: "11px",
                        letterSpacing: "0.32em",
                        textTransform: "uppercase",
                        color: dim,
                        margin: 0,
                        marginBottom: "28px",
                    }}
                >
                    Naomi Lennon · Creator Economy Operator
                </p>
                <h2
                    style={{
                        fontFamily: fontDisplay,
                        fontWeight: 300,
                        fontSize: "84px",
                        lineHeight: 0.98,
                        letterSpacing: "-0.02em",
                        margin: 0,
                        color: textLeft,
                    }}
                >
                    <span style={{ display: "block" }}>{line1}</span>
                    <span style={{ display: "block" }}>{line2}</span>
                    <span style={{ display: "block", fontStyle: "italic", color: "#B23B22" }}>
                        {line3}
                    </span>
                </h2>
                <p
                    style={{
                        fontFamily: fontMono,
                        fontSize: "10px",
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                        color: dim,
                        margin: 0,
                        marginTop: "32px",
                    }}
                >
                    15+ years · 6,000+ campaigns · acquired 2019
                </p>
            </div>

            {!isIvory && isSplit && (
                <div
                    style={{
                        flex: "0 0 40%",
                        backgroundColor: bgRight,
                        padding: "56px 64px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        color: textRight,
                        borderLeft: "1px solid rgba(255,255,255,0.08)",
                    }}
                >
                    <p
                        style={{
                            fontFamily: fontMono,
                            fontSize: "10px",
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            color: "rgba(253,251,247,0.5)",
                            margin: 0,
                            marginBottom: "18px",
                        }}
                    >
                        Founder
                    </p>
                    <p
                        style={{
                            fontFamily: fontDisplay,
                            fontStyle: "italic",
                            fontWeight: 300,
                            fontSize: "30px",
                            lineHeight: 1.18,
                            margin: 0,
                            color: textRight,
                        }}
                    >
                        <span style={{ display: "block" }}>{right1}</span>
                        <span
                            style={{
                                display: "block",
                                borderBottom: "2px solid #B23B22",
                                paddingBottom: "2px",
                                width: "fit-content",
                            }}
                        >
                            {right2}
                        </span>
                        <span style={{ display: "block" }}>{right3}</span>
                    </p>
                </div>
            )}

            {isInk && (
                <div
                    style={{
                        position: "absolute",
                        right: "64px",
                        bottom: "48px",
                        textAlign: "right",
                        color: "rgba(253,251,247,0.6)",
                    }}
                >
                    <p
                        style={{
                            fontFamily: fontDisplay,
                            fontStyle: "italic",
                            fontWeight: 300,
                            fontSize: "22px",
                            lineHeight: 1.2,
                            margin: 0,
                            maxWidth: "420px",
                        }}
                    >
                        {right1} {right2} {right3}
                    </p>
                </div>
            )}
        </div>
    );
}
